import { useCallback, useEffect, useRef, useState } from 'react';

import { useApolloClient } from '@apollo/client/react';
import { useSnackbar } from 'notistack';
import { useLatest } from 'react-use';
import { Apollo } from 'src/gql/generated';

import {
  getEditorSkill,
  getSkillEditorKey,
  getSkillWithLatestFileStates,
} from './skill-editor-draft';
import { getErrorMessage } from './skill-editor-errors';
import { getAttachmentFolderIds } from './skill-editor-status';
import type { SkillSaveQueue } from './skill-save-queue';
import type { PendingAction, SkillDraftStore } from './use-skill-draft-session';

/**
 * Changes to the skill made outside this editor: another tab, an AI edit approved in chat,
 * file processing finishing. Each is taken in when the editor holds no unsaved work, and
 * reported as a conflict when it does.
 */
export function useSkillRemoteSync({
  canEdit,
  initializeEditor,
  observedSkill,
  refetchSkill,
  saveQueue,
  saving,
  setExpandedFolders,
  setPendingAction,
  skill,
  skillId,
  store,
}: {
  canEdit: boolean;
  initializeEditor: (fetched: Apollo.AgentSkillSimpleFragment) => void;
  /**
   * What the cache holds of the skill: the author's draft revision, otherwise the published
   * version. Catalog writes can update the skill without its content or attachments.
   */
  observedSkill: Apollo.AgentSkillSimpleFragment | null | undefined;
  refetchSkill: () => Promise<unknown>;
  saveQueue: SkillSaveQueue;
  saving: boolean;
  setExpandedFolders: React.Dispatch<React.SetStateAction<string[]>>;
  setPendingAction: (action: PendingAction | null) => void;
  skill: Apollo.AgentSkillSimpleFragment | null;
  skillId: string;
  store: SkillDraftStore;
}) {
  const apolloClient = useApolloClient();
  const { enqueueSnackbar } = useSnackbar();
  const [reloading, setReloading] = useState(false);
  const loadingKeyRef = useRef<string | null>(null);
  const caughtUpRevisionRef = useRef<number | null>(null);
  const observedKey = observedSkill ? getSkillEditorKey(observedSkill) : null;
  const {
    editRevisionRef,
    editorGenerationRef,
    flushEditors,
    reportSaveError,
    skillRef,
    writeSkill,
  } = store;

  const reportOutdated = useCallback(
    (message: string) => reportSaveError({ message, outdated: true }),
    [reportSaveError]
  );

  /**
   * Load the version the cache now shows. The cache entry may be partial, so the editor
   * starts from a complete fetch, and only if nothing was edited while it ran.
   */
  async function takeObservedVersion(key: string) {
    if (saveQueue.getIsRunning()) return;
    if (store.initializedKeyRef.current === key || loadingKeyRef.current === key) return;
    flushEditors();
    if (store.getHasUnsavedChanges())
      return reportOutdated(
        'This skill was changed elsewhere. Reload to review the latest version.'
      );

    const revision = editRevisionRef.current;
    const generation = editorGenerationRef.current;
    // A newer observed version, or a reload, replaces this load.
    const isCurrent = () =>
      loadingKeyRef.current === key && generation === editorGenerationRef.current;
    loadingKeyRef.current = key;

    try {
      const loaded = await fetchLatestSkill({ apolloClient, skillId });
      flushEditors();
      if (!isCurrent()) return;
      if (
        revision !== editRevisionRef.current ||
        store.getHasUnsavedChanges() ||
        saveQueue.getIsRunning()
      )
        return reportOutdated(
          'This skill was changed elsewhere. Your draft was kept. Reload to review the latest version.'
        );
      if (!loaded) throw new Error('The skill could not be loaded.');
      if (store.initializedKeyRef.current === null)
        setExpandedFolders(
          getAttachmentFolderIds(getEditorSkill(loaded).attachments.map(file => file.path))
        );
      initializeEditor(loaded);
    } catch (error) {
      if (isCurrent()) reportOutdated(getErrorMessage(error, 'Could not load the skill.'));
    } finally {
      if (loadingKeyRef.current === key) loadingKeyRef.current = null;
    }
  }

  const takeObservedVersionRef = useLatest(takeObservedVersion);

  /** Replace the editor with the server's latest version, dropping unsaved edits. */
  const reloadSkill = useCallback(async () => {
    flushEditors();
    const revision = editRevisionRef.current;
    setReloading(true);
    try {
      const latest = await fetchLatestSkill({ apolloClient, skillId });
      if (!latest) throw new Error('The skill could not be loaded.');
      flushEditors();
      if (revision !== editRevisionRef.current)
        return reportOutdated('You edited the draft while reloading. Reload again to replace it.');
      initializeEditor(latest);
      setPendingAction(null);
    } catch {
      enqueueSnackbar('Could not reload the skill. Please try again.', { variant: 'error' });
    } finally {
      setReloading(false);
    }
  }, [
    apolloClient,
    editRevisionRef,
    enqueueSnackbar,
    flushEditors,
    initializeEditor,
    reportOutdated,
    setPendingAction,
    skillId,
  ]);

  const retryLoad = useCallback(() => {
    if (observedKey) void reloadSkill();
    else void refetchSkill().catch(() => undefined);
  }, [observedKey, refetchSkill, reloadSkill]);

  // Also delivers drafts saved elsewhere, such as an AI edit approved in chat.
  Apollo.useAgentSkillDraftChangedSubscription({ skip: !canEdit, variables: { skillId } });

  // File processing finishing elsewhere: take its states without reloading the editor.
  useEffect(() => {
    const current = skillRef.current;
    if (!current || !observedSkill) return;
    const next = getSkillWithLatestFileStates({ current, latest: observedSkill });
    if (next !== current) writeSkill(next);
  }, [observedSkill, skillRef, writeSkill]);

  // A file that finished before the subscription started would otherwise go unseen.
  const watchingFiles = canEdit && skill?.draft?.filesReady === false;
  useEffect(() => {
    const revision = skillRef.current?.draft?.revision;
    if (!watchingFiles || revision === undefined || caughtUpRevisionRef.current === revision)
      return;
    caughtUpRevisionRef.current = revision;
    void refetchSkill().catch(() => undefined);
  }, [refetchSkill, skillRef, watchingFiles]);

  // `saving` re-runs this when a save lands, so a version seen during the save is checked after it.
  useEffect(() => {
    if (observedKey) void takeObservedVersionRef.current(observedKey);
  }, [observedKey, saving, takeObservedVersionRef]);

  return { observedKey, reloadSkill, reloading, retryLoad };
}

/** The server's latest copy, bypassing the cache so the editor starts from a complete snapshot. */
async function fetchLatestSkill({
  apolloClient,
  skillId,
}: {
  apolloClient: ReturnType<typeof useApolloClient>;
  skillId: string;
}) {
  const { data } = await apolloClient.query<
    Apollo.GetAgentSkillQuery,
    Apollo.GetAgentSkillQueryVariables
  >({
    context: { queryDeduplication: false },
    errorPolicy: 'none',
    fetchPolicy: 'no-cache',
    query: Apollo.GetAgentSkillDocument,
    variables: { id: skillId },
  });
  return data?.agentSkill ?? null;
}
